import { Button } from "@/components/ui/button.tsx";
import { Separator } from "@/components/ui/separator";
import type { Instance } from "@/context/auth/AuthContext";
import { useAuth } from "@/context/auth/AuthContext";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";

type CmsSidebarVisibilityContext = {
  instanceData: Instance | undefined;
  advancedMode: boolean;
};

type CmsSidebarItem = {
  path: string;
  /** label i18n ; si absent, `label` (texte brut) est utilisé */
  labelKey?: string;
  label?: string;
  /** clé i18n à repli visible utilisée avec `label` (défaut si absente) */
  labelVisibleKey?: string;
  partialMatch?: boolean;
  isVisible?: (context: CmsSidebarVisibilityContext) => boolean;
};

type CmsSidebarGroup = {
  /** titre i18n ; si absent, `title` (texte brut) est utilisé */
  titleKey?: string;
  title?: string;
  items: CmsSidebarItem[];
};

const CMS_SIDEBAR_GROUPS: CmsSidebarGroup[] = [
  {
    titleKey: "cms.sidebar.settings",
    items: [
      {
        path: "/cms/general-settings",
        labelKey: "cms.generalSettings.tab.title",
      },
      {
        path: "/cms/apparence",
        labelKey: "cms.appearance.title",
      },
      {
        path: "/cms/maintenance",
        label: "Maintenance",
        labelVisibleKey: "bo.cms.sidebar.maintenance",
      },
    ],
  },
  {
    titleKey: "cms.sidebar.pages",
    items: [
      { path: "/cms", labelKey: "cmsTabAccueil" },
      { path: "/cms/artiste", labelKey: "cmsTabArtiste" },
      {
        path: "/cms/selection",
        labelKey: "cmsTabSelection",
        partialMatch: true,
      },
      { path: "/cms/news", labelKey: "cmsTabNews" },
      { path: "/cms/expertise", labelKey: "cmsTabExpertise" },
      {
        path: "/cms/artwork-authentication",
        labelKey: "cmsTabAuthenticationForm",
      },
      { path: "/cms/about-us", labelKey: "cmsTabAPropos" },
      { path: "/cms/contact", labelKey: "cmsTabContact" },
      { path: "/cms/policies", labelKey: "cmsTabPagesLegales" },
    ],
  },
  {
    titleKey: "cms.sidebar.catalogueRaisonne",
    items: [
      {
        path: "/cms/catalogue-raisonne",
        labelKey: "cmsTabCatalogueRaisonne",
        isVisible: ({ instanceData }) => instanceData?.hideCr !== true,
      },
      {
        path: "/cms/authentification",
        labelKey: "cmsTabAuthentification",
        isVisible: ({ instanceData }) =>
          !(instanceData?.hideCr === true && instanceData?.haveEshop === false),
      },
    ],
  },
  {
    titleKey: "cms.sidebar.eshop",
    items: [
      {
        path: "/cms/e-shop",
        labelKey: "cms.tab.eshop",
        isVisible: ({ instanceData }) => instanceData?.haveEshop === true,
      },
    ],
  },
  {
    // CMS avancé — visible uniquement quand le mode avancé est activé.
    title: "Avancé",
    items: [
      {
        path: "/cms/advanced/accueil",
        label: "CMS avancé",
        partialMatch: true,
        isVisible: ({ advancedMode }) => advancedMode,
      },
    ],
  },
];

function CmsSidebarLink({
  path,
  label,
  isActive,
  onNavigate,
}: {
  path: string;
  label: string;
  isActive: boolean;
  onNavigate: (path: string) => void;
}) {
  return (
    <Button
      onClick={() => onNavigate(path)}
      variant={isActive ? "default" : "not_selected"}
      className="self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex"
    >
      <p className="ml-4 text-sm font-semibold font-['Inter'] leading-tight">
        {label}
      </p>
    </Button>
  );
}

const CmsSidebarNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { instanceData } = useAuth();
  const advancedMode = instanceData?.CMS?.advancedCmsEnabled ?? false;
  const { t } = useTranslation();
  const vt = useVisibleT();

  const isActive = (path: string, partial = false) =>
    partial ? location.pathname.startsWith(path) : location.pathname === path;

  const visibilityContext: CmsSidebarVisibilityContext = {
    instanceData,
    advancedMode,
  };

  const visibleGroups = CMS_SIDEBAR_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => item.isVisible?.(visibilityContext) ?? true,
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="flex-col justify-start items-start gap-1 inline-flex">
      {visibleGroups.map((group, groupIndex) => (
        <div
          key={group.titleKey ?? group.title}
          className="flex flex-col gap-1 w-full"
        >
          {groupIndex > 0 && <Separator className="my-2 h-[3px]" />}
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-3 pt-3 pb-1">
            {group.titleKey
              ? t(group.titleKey)
              : vt("bo.cms.sidebar.advanced", group.title ?? "")}
          </p>
          {group.items.map((item) => (
            <CmsSidebarLink
              key={item.path}
              path={item.path}
              label={
                item.labelKey
                  ? t(item.labelKey)
                  : item.label
                    ? vt(
                        item.labelVisibleKey ?? "bo.cms.sidebar.advancedPuck",
                        item.label,
                      )
                    : item.path
              }
              isActive={isActive(item.path, item.partialMatch)}
              onNavigate={navigate}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export default CmsSidebarNav;
